import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

export type DocumentStatus =
  | 'uploaded'
  | 'analyzing'
  | 'scheduled'
  | 'processing'
  | 'processed'
  | 'failed'

interface StatusBadgeProps {
  status: DocumentStatus | string
}

const statusConfig: Record<string, { label: string; className: string }> = {
  uploaded: { label: 'Uploaded', className: 'bg-muted text-muted-foreground' },
  analyzing: { label: 'Analyzing', className: 'bg-info/10 text-info' },
  scheduled: { label: 'Pending', className: 'bg-info/10 text-info' },
  processing: { label: 'Processing', className: 'bg-warning/10 text-warning' },
  processed: { label: 'Processed', className: 'bg-success/10 text-success' },
  failed: { label: 'Failed', className: 'bg-destructive/10 text-destructive' },
  pending: { label: 'Pending', className: 'bg-muted text-muted-foreground' },
  indexed: { label: 'Indexed', className: 'bg-success/10 text-success' },
  skipped: { label: 'Skipped', className: 'bg-muted text-muted-foreground' },
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.uploaded
  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  )
}

/** A document's file type, on a row that lists documents. */
export function FileTypeBadge({ fileType }: { fileType: string }) {
  return (
    <Badge variant="muted" className="h-auto rounded px-1.5 text-3xs uppercase tracking-wide">
      {fileType}
    </Badge>
  )
}

export type SourceType = 'lake' | 'upload'

interface SourceBadgeProps {
  source: SourceType
}

const sourceConfig: Record<SourceType, { label: string; className: string }> = {
  lake: { label: 'Lake', className: 'bg-violet-500/10 text-violet-500' },
  upload: { label: 'Uploaded', className: 'bg-muted text-muted-foreground' },
}

export function SourceBadge({ source }: SourceBadgeProps) {
  const config = sourceConfig[source] || sourceConfig.upload
  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-xs font-medium ${config.className}`}
    >
      {config.label}
    </span>
  )
}

interface FilterPillProps {
  label: string
  onRemove: () => void
}

export function FilterPill({ label, onRemove }: FilterPillProps) {
  return (
    <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-3xs font-medium bg-primary/10 text-primary border border-primary/20">
      {label}
      <button onClick={onRemove} className="ml-0.5 hover:bg-primary/20 rounded-sm p-0.5">
        <X className="size-2.5" />
      </button>
    </span>
  )
}

interface FilterPillsProps {
  filters: Array<{ key: string; label: string }>
  onRemove: (key: string) => void
  onClearAll: () => void
}

export function FilterPills({ filters, onRemove, onClearAll }: FilterPillsProps) {
  if (filters.length === 0) return null
  return (
    <div className="flex items-center gap-2">
      {filters.map(filter => (
        <FilterPill key={filter.key} label={filter.label} onRemove={() => onRemove(filter.key)} />
      ))}
      {filters.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="h-5 px-1.5 text-3xs text-muted-foreground hover:text-foreground"
        >
          Clear all
        </Button>
      )}
    </div>
  )
}
